/**
 * crewRoster.js — Single source of truth for the company's crew roster.
 *
 * Historically the roster lived in two parallel places:
 *   - A hardcoded `CREW` array in lib/crewConfig.js (used by Crew Clock,
 *     Schedule's AssignmentDialog, HistoryTable, EditTimeEntryDialog, and
 *     timeEntriesData.js for rate lookups).
 *   - The `settings.crew_rates` JSONB in Supabase (edited via Settings →
 *     Crew Rates UI, used by the Crew Portal + portal admin).
 *
 * Those two diverged: adding an employee in Settings did NOT make them
 * clockable, schedulable, or rate-lookup-able. This module consolidates
 * everything onto `settings.crew_rates` and exposes a tiny cache + hook so
 * the data is shared across surfaces with no double-fetch.
 *
 * Storage shape (one entry inside the `settings.crew_rates` JSONB array):
 *   {
 *     id,                       // stable per-crew identifier
 *     name, role, rate,         // original onboarding fields
 *     phone, email,             // Phase 1
 *     hire_date,                // 'YYYY-MM-DD'
 *     emergency_contact_name,
 *     emergency_contact_phone,
 *     status,                   // 'active' | 'inactive' | 'laid_off' | 'terminated'
 *     status_effective_date,    // 'YYYY-MM-DD' (auto-stamped on laid_off/terminated)
 *     type,                     // 'crew' | 'sub' (subcontractor, paid hourly)
 *     company,                  // sub's business name ('' for crew)
 *   }
 *
 * The portal access flag (`settings.crew_portals[id].active`) is mirrored
 * from `status === 'active'` by `saveCrewMember()` so a single status field
 * gates both clock-in and portal login.
 */

import { useCallback, useEffect, useState } from 'react';
import { fetchSettingsByKey, upsertSettingsByKey } from '@/lib/settingsData';

/* ----------------------------------------------------------------------------
 * Public constants
 * ------------------------------------------------------------------------- */

/** Canonical status ids (ordered: active first → terminated last). */
export const CREW_STATUSES = ['active', 'inactive', 'laid_off', 'terminated'];

/** Default status for a newly-onboarded employee. */
export const DEFAULT_CREW_STATUS = 'active';

/* ----------------------------------------------------------------------------
 * Normalization
 * ------------------------------------------------------------------------- */

const safeStr = (v) => (v == null ? '' : String(v));

const deriveInitials = (name) => {
  const parts = safeStr(name).trim().split(/\s+/).filter(Boolean);
  const a = parts[0]?.[0] || '';
  const b = parts[1]?.[0] || '';
  return (a + b).toUpperCase() || 'XX';
};

/**
 * Return a fully-shaped crew record from a partial/legacy input. Always
 * defines every operational field so the UI never has to handle `undefined`.
 */
export const normalizeCrewMember = (raw) => {
  const status = CREW_STATUSES.includes(raw?.status)
    ? raw.status
    : DEFAULT_CREW_STATUS;
  return {
    id: safeStr(raw?.id),
    name: safeStr(raw?.name),
    role: safeStr(raw?.role) || 'Crew',
    rate: Number(raw?.rate) || 0,
    phone: safeStr(raw?.phone),
    email: safeStr(raw?.email),
    hire_date: safeStr(raw?.hire_date),
    emergency_contact_name: safeStr(raw?.emergency_contact_name),
    emergency_contact_phone: safeStr(raw?.emergency_contact_phone),
    status,
    status_effective_date: safeStr(raw?.status_effective_date),
    // 'crew' (employee) or 'sub' (subcontractor, paid hourly). The DB stamps
    // each time entry with this at the moment it's logged (migration 084).
    type: raw?.type === 'sub' ? 'sub' : 'crew',
    company: safeStr(raw?.company),
    initials: deriveInitials(raw?.name),
  };
};

export const isSub = (member) => member?.type === 'sub';

export const isActiveCrew = (member) =>
  (member?.status || DEFAULT_CREW_STATUS) === 'active';

export const filterActiveCrew = (roster = []) => (roster || []).filter(isActiveCrew);

/* ----------------------------------------------------------------------------
 * In-memory cache + inflight dedupe
 * ------------------------------------------------------------------------- */

const CACHE_TTL_MS = 30 * 1000;

const _cache = {
  ts: 0,
  data: [],
};
let _inflight = null;

/** Subscriber callbacks notified whenever the cache is updated. */
const _subscribers = new Set();
const _notify = () => {
  _subscribers.forEach((fn) => {
    try {
      fn(_cache.data);
    } catch (_) {
      /* swallow — subscriber bugs shouldn't break the cache */
    }
  });
};

/**
 * Subscribe to roster changes. Returns an unsubscribe function.
 * Used by `useCrewRoster()` so EVERY consumer instantly re-renders when ANY
 * surface mutates the roster (e.g. Settings → Crew Rates "Save" should
 * cause Crew Clock to add a freshly-added employee with no manual refresh).
 */
export const subscribeToCrewRoster = (fn) => {
  if (typeof fn !== 'function') return () => {};
  _subscribers.add(fn);
  return () => _subscribers.delete(fn);
};

/** Synchronous accessor — returns whatever's in the cache right now. */
export const getCachedCrewRoster = () => _cache.data;

/** Force the next loadCrewRoster() call to refetch. */
export const invalidateCrewRoster = () => {
  _cache.ts = 0;
};

/**
 * Load the roster from Supabase, returning the cached copy if it's fresh
 * enough. Concurrent callers share one in-flight request.
 */
export const loadCrewRoster = async ({ force = false } = {}) => {
  if (!force && Date.now() - _cache.ts < CACHE_TTL_MS && _cache.data.length) {
    return _cache.data;
  }
  if (_inflight) return _inflight;
  _inflight = (async () => {
    try {
      const { value } = await fetchSettingsByKey('crew_rates', []);
      const list = Array.isArray(value)
        ? value.map(normalizeCrewMember).filter((m) => m.id || m.name)
        : [];
      _cache.ts = Date.now();
      _cache.data = list;
      _notify();
      return list;
    } finally {
      _inflight = null;
    }
  })();
  return _inflight;
};

/* ----------------------------------------------------------------------------
 * Lookups
 * ------------------------------------------------------------------------- */

export const findCrewMember = (crewId, roster = _cache.data) =>
  (roster || []).find((c) => c.id === crewId) || null;

export const findCrewByName = (name, roster = _cache.data) => {
  const lower = safeStr(name).toLowerCase();
  return (roster || []).find((c) => safeStr(c.name).toLowerCase() === lower) || null;
};

/**
 * Async single-member lookup — used by non-React helpers (e.g.
 * timeEntriesData.js rate lookups). Hits the cache first; only fetches
 * Supabase if the cache is cold.
 */
export const getCrewMember = async (crewId) => {
  if (!crewId) return null;
  let hit = findCrewMember(crewId);
  if (hit) return hit;
  await loadCrewRoster();
  return findCrewMember(crewId);
};

/* ----------------------------------------------------------------------------
 * Writes — full-roster replacement (used by Settings → Crew Rates save)
 * ------------------------------------------------------------------------- */

/**
 * Persist a complete roster array to settings.crew_rates and update the
 * cache. Every consumer subscribed via `useCrewRoster()` re-renders.
 */
export const saveCrewRoster = async (roster) => {
  const normalized = (roster || []).map(normalizeCrewMember);
  await upsertSettingsByKey('crew_rates', normalized);
  _cache.ts = Date.now();
  _cache.data = normalized;
  _notify();
  return normalized;
};

/* ----------------------------------------------------------------------------
 * React hook
 * ------------------------------------------------------------------------- */

/**
 * useCrewRoster — subscribes to the shared cache and triggers a load on
 * first mount. Pass `{ activeOnly: true }` to filter out non-active crew.
 *
 * Returns: { roster, allRoster, loading, refresh, error }
 *   - `roster`     — the array consumers should render (filtered if requested)
 *   - `allRoster`  — the unfiltered array (handy when the page needs both)
 *   - `loading`    — true ONLY on the very first load when the cache is cold
 *   - `refresh()`  — force-refetch + notify all subscribers
 *   - `error`      — last error string, or null
 */
export const useCrewRoster = ({ activeOnly = false } = {}) => {
  const [roster, setRoster] = useState(getCachedCrewRoster());
  const [loading, setLoading] = useState(getCachedCrewRoster().length === 0);
  const [error, setError] = useState(null);

  useEffect(() => {
    let alive = true;
    const unsubscribe = subscribeToCrewRoster((next) => {
      if (alive) setRoster(next);
    });
    loadCrewRoster()
      .then((list) => {
        if (alive) setRoster(list);
      })
      .catch((e) => {
        if (alive) setError(e?.message || 'Failed to load crew roster');
      })
      .finally(() => {
        if (alive) setLoading(false);
      });
    return () => {
      alive = false;
      unsubscribe();
    };
  }, []);

  const refresh = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const fresh = await loadCrewRoster({ force: true });
      return fresh;
    } catch (e) {
      setError(e?.message || 'Failed to refresh crew roster');
      throw e;
    } finally {
      setLoading(false);
    }
  }, []);

  const filtered = activeOnly ? filterActiveCrew(roster) : roster;
  return { roster: filtered, allRoster: roster, loading, refresh, error };
};
